import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { QuestionModal } from '@/components/QuestionModal';
import { DeleteConfirmModal } from '@/components/DeleteConfirmModal';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Trash2, Edit, Loader2, CheckCircle, XCircle, Image } from 'lucide-react';
import { subjectsApi, topicsApi, questionsApi } from '@/services/api';

export default function Questions() {
  const { topicId } = useParams();
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [selectedTopicId, setSelectedTopicId] = useState(topicId || '');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  const currentSubject = subjects.find(s => s._id === selectedSubjectId);
  const currentTopic = topics.find(t => t._id === selectedTopicId);

  useEffect(() => {
    loadSubjects();
  }, []);

  useEffect(() => {
    if (selectedSubjectId) {
      loadTopics();
    } else {
      setTopics([]);
      setSelectedTopicId('');
    }
  }, [selectedSubjectId]);

  useEffect(() => {
    if (selectedTopicId) {
      loadQuestions();
    } else {
      setQuestions([]);
    }
  }, [selectedTopicId]);

  const loadSubjects = async () => {
    try {
      const res = await subjectsApi.getAll();
      const loadedSubjects = res.data || [];
      setSubjects(loadedSubjects);
      
      if (topicId && loadedSubjects.length > 0) {
        // Find subject for the topicId
        for (const subject of loadedSubjects) {
          const topicsRes = await topicsApi.getBySubject(subject._id);
          const foundTopic = (topicsRes.data || []).find(t => t._id === topicId);
          if (foundTopic) {
            setSelectedSubjectId(subject._id);
            setSelectedTopicId(topicId);
            break;
          }
        }
      } else if (loadedSubjects.length > 0) {
        setSelectedSubjectId(loadedSubjects[0]._id);
      }
    } catch (error) {
      toast.error('Dersler yüklenemedi');
    }
  };

  const loadTopics = async () => {
    if (!selectedSubjectId) return;
    try {
      const res = await topicsApi.getBySubject(selectedSubjectId);
      const loadedTopics = res.data || [];
      setTopics(loadedTopics);
      if (!selectedTopicId && loadedTopics.length > 0) {
        setSelectedTopicId(loadedTopics[0]._id);
      }
    } catch (error) {
      toast.error('Konular yüklenemedi');
    }
  };

  const loadQuestions = async () => {
    if (!selectedTopicId) return;
    try {
      setLoading(true);
      const res = await questionsApi.getByTopic(selectedTopicId);
      setQuestions(res.data || []);
    } catch (error) {
      toast.error('Sorular yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const filteredQuestions = useMemo(() => {
    if (!search) return questions;
    return questions.filter(q => 
      q.title.toLowerCase().includes(search.toLowerCase())
    );
  }, [questions, search]);

  const handleSave = async (data) => {
    try {
      if (editingQuestion) {
        await questionsApi.update(editingQuestion._id, data);
        toast.success('Soru güncellendi');
      } else {
        await questionsApi.create(data);
        toast.success('Soru oluşturuldu');
      }
      setModalOpen(false);
      setEditingQuestion(null);
      loadQuestions();
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleDelete = async () => {
    try {
      await Promise.all(selected.map(id => questionsApi.delete(id)));
      toast.success(`${selected.length} soru silindi`);
      setSelected([]);
      setDeleteModalOpen(false);
      loadQuestions();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selected.length === filteredQuestions.length) {
      setSelected([]);
    } else {
      setSelected(filteredQuestions.map(q => q._id));
    }
  };

  const breadcrumbs = [
    { label: 'Dersler', href: '/subjects' },
    ...(currentSubject ? [{ label: currentSubject.name, href: `/subjects/${currentSubject._id}/topics` }] : []),
    ...(currentTopic ? [{ label: currentTopic.name }] : []),
    { label: 'Sorular' },
  ];

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <div className="space-y-6" data-testid="questions-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Sorular
            </h1>
            <p className="text-slate-500">Sınav sorularını yönetin</p>
          </div>
          <Button 
            onClick={() => { setEditingQuestion(null); setModalOpen(true); }}
            className="bg-teal-700 hover:bg-teal-800"
            disabled={!selectedTopicId}
            data-testid="add-question-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Yeni Soru
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Select value={selectedSubjectId} onValueChange={(v) => { setSelectedSubjectId(v); setSelectedTopicId(''); }}>
            <SelectTrigger className="w-full sm:w-56" data-testid="subject-filter">
              <SelectValue placeholder="Ders seçin" />
            </SelectTrigger>
            <SelectContent>
              {subjects.map((subject) => (
                <SelectItem key={subject._id} value={subject._id}>
                  {subject.icon} {subject.name} 
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedTopicId} onValueChange={setSelectedTopicId} disabled={!selectedSubjectId}>
            <SelectTrigger className="w-full sm:w-56" data-testid="topic-filter">
              <SelectValue placeholder="Konu seçin" />
            </SelectTrigger>
            <SelectContent>
              {topics.map((topic) => (
                <SelectItem key={topic._id} value={topic._id}>
                  {topic.name} 
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Soru ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-questions"
            />
          </div>

          {selected.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-500">{selected.length} seçili</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteModalOpen(true)}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="bulk-delete-questions-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Sil
              </Button>
            </div>
          )}
        </div>

        {/* Select All */}
        {filteredQuestions.length > 0 && (
          <div className="flex items-center gap-2">
            <Checkbox
              checked={selected.length === filteredQuestions.length && filteredQuestions.length > 0}
              onCheckedChange={toggleSelectAll}
              data-testid="select-all-questions"
            />
            <span className="text-sm text-slate-500">Tümünü seç</span>
          </div>
        )}

        {/* Content */}
        {!selectedTopicId ? (
          <Card className="p-12 text-center">
            <p className="text-slate-500">Lütfen bir konu seçin</p>
          </Card>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : filteredQuestions.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="text-6xl mb-4">❓</div>
            <h3 className="text-lg font-semibold text-slate-900">Henüz soru yok</h3>
            <p className="text-slate-500 mt-1">Bu konuya yeni bir soru ekleyin</p>
            <Button 
              onClick={() => setModalOpen(true)}
              className="mt-4 bg-teal-700 hover:bg-teal-800"
            >
              <Plus className="w-4 h-4 mr-2" /> İlk Soruyu Ekle
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredQuestions.map((question, idx) => (
              <Card 
                key={question._id}
                className="hover:border-teal-200 transition-colors"
                data-testid={`question-item-${question._id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <Checkbox
                      checked={selected.includes(question._id)}
                      onCheckedChange={() => toggleSelect(question._id)}
                      className="mt-1"
                      data-testid={`select-question-${question._id}`}
                    />
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-slate-600">{idx + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 line-clamp-2">{question.title}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {question.imageUrl && (
                          <Badge variant="outline" className="gap-1">
                            <Image className="w-3 h-3" /> Görsel
                          </Badge>
                        )}
                        <Badge variant="secondary">
                          {question.options?.length || 0} seçenek
                        </Badge>
                        {question.options?.some(o => o.isCorrect) ? (
                          <Badge variant="outline" className="text-green-600 border-green-200">
                            <CheckCircle className="w-3 h-3 mr-1" /> Cevap var
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-600 border-amber-200">
                            <XCircle className="w-3 h-3 mr-1" /> Cevap yok
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => { setEditingQuestion(question); setModalOpen(true); }}
                      data-testid={`edit-question-${question._id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <QuestionModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingQuestion(null); }}
        onSave={handleSave}
        question={editingQuestion}
        topicId={selectedTopicId}
      />

      <DeleteConfirmModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        count={selected.length}
      />
    </Layout>
  );
}
